/**
 * API Service for Calamus Education
 * Handles all API calls.
 * Production build uses window.__API_BASE_URL__ from config.js (e.g. https://domain.com/calamus-v3/public/api).
 * Dev: set REACT_APP_API_BASE_URL in .env or use default.
 */
const API_BASE_URL =
  (typeof window !== 'undefined' && window.__API_BASE_URL__) ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://www.calamuseducation.com/calamus-v3/public/api';
const logApiRequest = (method, endpoint) => {
  console.log(`[API] ${method} ${API_BASE_URL}${endpoint}`, {
    API_BASE_URL,
    endpoint,
  });
};

const GET_DEFAULT_TTL_MS = 750;
const inFlightRequests = new Map();
const responseCache = new Map();
const cooldownUntilByEndpoint = new Map();

const getEndpointBase = (endpoint) => String(endpoint || '').split('?')[0] || '';

const getCacheTtlMsForEndpoint = (endpoint) => {
  const base = getEndpointBase(endpoint);
  if (base === '/notifications/get') return 5000;
  if (base === '/friends/get-requests') return 8000;
  if (base === '/friends/people-you-may-know') return 30000;
  if (base === '/friends/get-friends') return 5000;
  if (base === '/chat/conversations') return 2000;
  if (base === '/chat/messages') return 1000;
  return GET_DEFAULT_TTL_MS;
};

const buildRequestKey = ({ method, endpoint, token }) => {
  const t = token ? String(token) : '';
  const m = method ? String(method) : 'GET';
  const e = endpoint ? String(endpoint) : '';
  return `${m}::${t}::${e}`;
};

const throwIfInCooldown = (endpoint, token) => {
  const key = `${token ? String(token) : ''}::${getEndpointBase(endpoint)}`;
  const until = cooldownUntilByEndpoint.get(key) || 0;
  if (until > Date.now()) {
    const error = new Error('Rate limited');
    error.status = 429;
    throw error;
  }
};

const setCooldownFromResponse = (endpoint, token, response) => {
  const retryAfterHeader = response?.headers?.get?.('Retry-After');
  const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : Number.NaN;
  const retryAfterMs = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds * 1000 : 5000;
  const until = Date.now() + Math.max(5000, retryAfterMs);
  const key = `${token ? String(token) : ''}::${getEndpointBase(endpoint)}`;
  cooldownUntilByEndpoint.set(key, until);
};

/**
 * Document lesson HTML URL.
 * Files are stored at origin/uploads/lessons/html/{lessonId}.html
 */
export const getLessonDocumentUrl = (lessonId) => {
  let origin = '';

  try {
    origin = new URL(API_BASE_URL).origin;
  } catch {
    origin = typeof window !== 'undefined' ? window.location.origin : '';
  }

  return `${origin}/uploads/lessons/html/${Number(lessonId)}.html`;
};

/**
 * Get stored auth token from localStorage
 */
const getToken = () => localStorage.getItem('calamus_token');

/**
 * Generic GET fetch wrapper with error handling
 */
const fetchAPI = async (endpoint) => {
  const token = getToken();
  throwIfInCooldown(endpoint, token);

  const key = buildRequestKey({ method: 'GET', endpoint, token });
  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  const existing = inFlightRequests.get(key);
  if (existing) return existing;

  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestPromise = (async () => {
      logApiRequest('GET', endpoint);
      const response = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        setCooldownFromResponse(endpoint, token, response);
      }

      if (!response.ok) {
        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.response = { data };
        throw error;
      }

      if (!data.success) {
        const error = new Error(data.error || 'API request failed');
        error.response = { data };
        throw error;
      }

      const ttlMs = getCacheTtlMsForEndpoint(endpoint);
      responseCache.set(key, { expiresAt: Date.now() + ttlMs, value: data });
      return data;
    })();

    inFlightRequests.set(key, requestPromise);
    return await requestPromise;
  } catch (error) {
    // Don't log 404 errors as they're expected for missing resources
    if (error.status !== 404 && !error.message?.includes('404')) {
      console.error(`API Error (${endpoint}):`, error);
    }
    throw error;
  } finally {
    inFlightRequests.delete(key);
  }
};

/**
 * POST fetch wrapper with JSON body
 */
const postAPI = async (endpoint, body = {}) => {
  const token = getToken();
  throwIfInCooldown(endpoint, token);

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    logApiRequest('POST', endpoint);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 429) {
      setCooldownFromResponse(endpoint, token, response);
    }

    if (!response.ok) {
      const error = new Error(data.error || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }

    return data;
  } catch (error) {
    if (error.message !== 'Not authenticated') {
      console.error(`API Error (${endpoint}):`, error);
    }
    throw error;
  }
};

/**
 * POST with form-urlencoded body (for APIs that read $_POST, e.g. chat)
 */
const postFormAPI = async (endpoint, body = {}) => {
  const params = new URLSearchParams();
  Object.keys(body).forEach((key) => {
    if (body[key] != null && body[key] !== '') {
      params.append(key, body[key]);
    }
  });
  
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const token = getToken();
  throwIfInCooldown(endpoint, token);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  logApiRequest('POST', endpoint);
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: params.toString(),
  });
  const data = await response.json().catch(() => ({}));
  if (response.status === 429) {
    setCooldownFromResponse(endpoint, token, response);
  }
  if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
  if (!data.success) throw new Error(data.error || 'API request failed');
  return data;
};

/**
 * POST with multipart/form-data body (for file uploads)
 */
const postFormDataAPI = async (endpoint, formData) => {
  const token = getToken();
  throwIfInCooldown(endpoint, token);

  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    logApiRequest('POST', endpoint);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 429) {
      setCooldownFromResponse(endpoint, token, response);
    }

    if (!response.ok) {
      // Prioritize data.message if available (e.g. "This transaction ID has already been submitted.")
      // Then try data.error, then fallback to HTTP status
      throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
    }

    if (!data.success) {
      throw new Error(data.message || data.error || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

/**
 * DELETE fetch wrapper (includes Bearer token when available)
 */
const deleteAPI = async (endpoint) => {
  const token = getToken();
  throwIfInCooldown(endpoint, token);

  try {
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    logApiRequest('DELETE', endpoint);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
    });
    if (response.status === 429) {
      setCooldownFromResponse(endpoint, token, response);
    }
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (!data.success) throw new Error(data.error || 'API request failed');
    return data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

/**
 * Authenticated GET fetch wrapper (includes Bearer token)
 */
const authFetchAPI = async (endpoint) => {
  const token = getToken();
  if (!token) {
    const error = new Error('Not authenticated');
    error.status = 401;
    throw error;
  }

  throwIfInCooldown(endpoint, token);

  const key = buildRequestKey({ method: 'GET', endpoint, token });
  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  const existing = inFlightRequests.get(key);
  if (existing) return existing;

  try {
    const requestPromise = (async () => {
      logApiRequest('GET', endpoint);
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        setCooldownFromResponse(endpoint, token, response);
      }

      if (!response.ok) {
        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'API request failed');
      }

      const ttlMs = getCacheTtlMsForEndpoint(endpoint);
      responseCache.set(key, { expiresAt: Date.now() + ttlMs, value: data });
      return data;
    })();

    inFlightRequests.set(key, requestPromise);
    return await requestPromise;
  } catch (error) {
    if (error.message !== 'Not authenticated') {
      console.error(`API Error (${endpoint}):`, error);
    }
    throw error;
  } finally {
    inFlightRequests.delete(key);
  }
};

/**
 * Course API endpoints
 */
export const courseAPI = {
  /**
   * Get featured courses (top rated)
   */
  getFeatured: () => fetchAPI('/courses/featured'),
  
  /**
   * Get new courses (latest)
   */
  getNew: () => fetchAPI('/courses/new'),
  
  /**
   * Get all courses with optional filter
   * @param {string} major - Optional filter by major (english/korea)
   */
  getAll: (major = null) => {
    const params = major ? `?major=${major}` : '';
    return fetchAPI(`/courses${params}`);
  },
  
  /**
   * Get course detail by ID
   * @param {number} id - Course ID
   */
  getDetail: (id) => {
    return fetchAPI(`/courses/detail?id=${id}`);
  },
  /**
   * Get course curriculum (sections and lessons)
   * @param {number} courseId - Course ID
   */
  getCurriculum: (courseId) => {
    return fetchAPI(`/courses/curriculum?courseId=${courseId}`);
  },
  getCertificate: (courseId, userId) =>
    fetchAPI(`/courses/get-certificate?courseId=${courseId}&userId=${userId}`),
};

/**
 * Lesson API endpoints
 */
export const lessonAPI = {
  /**
   * Get lesson detail with course context
   * @param {number} lessonId
   * @param {number} courseId
   * @param {string} userId - Optional user ID for learned status
   */
  getDetail: (lessonId) => {
    return fetchAPI(`/lessons/detail?id=${lessonId}`).then((r) => r.data);
  },
  
  /**
   * Mark lesson as learned
   * @param {number} lessonId
   */
  markLearned: (lessonId) => postAPI('/lessons/mark-learned', { lessonId }),
  /**
   * Like/Unlike a lesson (toggle)
   * @param {number} lessonId
   */
  like: (lessonId) => postAPI('/lessons/like', { lessonId }),
  /**
   * Share a lesson
   * @param {number} lessonId
   */
  share: (lessonId) => postAPI('/lessons/share', { lessonId }),
  /**
   * Create a lesson comment or reply
   * @param {object} payload
   * @param {number} payload.lessonId
   * @param {string} [payload.body]
   * @param {string} [payload.image]
   * @param {number} [payload.parent]
   */
  comment: ({ lessonId, body = '', image = '', parent = 0 }) =>
    postAPI('/lessons/comment', {
      lessonId,
      body,
      image,
      parent,
    }),
};

export const getLessonShareUrl = (lessonId, courseId = null) => {
  if (!lessonId) return '';

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const basePath =
    (typeof window !== 'undefined' && window.__BASE_PATH__) ||
    process.env.PUBLIC_URL ||
    '';
  const normalizedBasePath = basePath
    ? `/${String(basePath).replace(/^\/+|\/+$/g, '')}`
    : '';

  const lessonPath = courseId
    ? `/course/${encodeURIComponent(String(courseId))}/lesson/${encodeURIComponent(String(lessonId))}`
    : `/watch/${encodeURIComponent(String(lessonId))}`;

  return origin ? `${origin}${normalizedBasePath}${lessonPath}` : `${normalizedBasePath}${lessonPath}`;
};

/**
 * Instructor API endpoints
 */
export const instructorAPI = {
  /**
   * Get all instructors with stats
   */
  getAll: () => fetchAPI('/instructors/all'),
  
  /**
   * Get instructor detail by ID
   * @param {number} id - Instructor ID
   */
  getDetail: (id) => fetchAPI(`/instructors/detail?id=${id}`),
};

/**
 * Video Channel API endpoints
 */
export const videoChannelAPI = {
  /**
   * Get video channel data
   * @param {string} channel - Channel name
   * @param {number} appId - App ID
   */
  get: (channel, appId) => fetchAPI(`/video-channel/get?channel=${channel}&app=${appId}`),
  
  /**
   * Get single video details with related videos
   * @param {number} id - Lesson/Video ID
   */
  getVideo: (id) => fetchAPI(`/video-channel/video?id=${id}`),
};

/**
 * Song API endpoints
 */
export const songAPI = {
  /**
   * Get songs with popular songs, all songs, and artists
   * @param {string} category - Category (english/korea)
   * @param {number} page - Page number for pagination
   * @param {string} userId - Optional user ID for liked state
   * @param {number|null} limit - Optional page size
   * @param {boolean|null} compact - Optional hint to return only paginated songs (if supported by backend)
   */
  get: (category = 'english', page = 1, userId = null, limit = null, compact = null) => {
    let url = `/songs/get?category=${category}&page=${page}`;
    if (userId) url += `&userId=${encodeURIComponent(userId)}`;
    if (limit != null) url += `&limit=${encodeURIComponent(String(limit))}`;
    if (compact != null) url += `&compact=${compact ? '1' : '0'}`;
    return fetchAPI(url);
  },

  /**
   * Get lyrics for a song
   * @param {string} url - Song URL identifier
   */
  getLyrics: (url) => fetchAPI(`/songs/lyrics?url=${url}`),

  /**
   * Get songs by artist
   * @param {object|number|string} paramsOrArtistId
   *   - object form: { artistId, category, page, limit, userId, artist }
   *   - legacy fallback: { artist: string } (if backend still supports artist name)
   */
  getByArtist: (paramsOrArtistId, category = 'english', page = 1, limit = 20, userId = null) => {
    const params = new URLSearchParams();
    if (paramsOrArtistId && typeof paramsOrArtistId === 'object') {
      if (paramsOrArtistId.artistId != null) params.set('artistId', String(paramsOrArtistId.artistId));
      if (paramsOrArtistId.artist != null) params.set('artist', String(paramsOrArtistId.artist));
      if (paramsOrArtistId.category != null) params.set('category', String(paramsOrArtistId.category));
      if (paramsOrArtistId.page != null) params.set('page', String(paramsOrArtistId.page));
      if (paramsOrArtistId.limit != null) params.set('limit', String(paramsOrArtistId.limit));
      if (paramsOrArtistId.userId != null) params.set('userId', String(paramsOrArtistId.userId));
    } else {
      if (paramsOrArtistId != null) params.set('artistId', String(paramsOrArtistId));
      if (category != null) params.set('category', String(category));
      if (page != null) params.set('page', String(page));
      if (limit != null) params.set('limit', String(limit));
      if (userId != null) params.set('userId', String(userId));
    }
    return fetchAPI(`/songs/by-artist?${params.toString()}`);
  },

  /**
   * Toggle like for a song (requires auth). Returns { liked, likeCount }.
   * @param {number} songId - Song id (songs.id)
   */
  like: (songId) => postAPI('/songs/like', { songId: songId }),

  /**
   * Increment download count for a song. Returns { downloadCount }.
   * @param {number} songId - Song id (songs.id)
   */
  download: (songId) => postAPI('/songs/download', { songId: songId }),
};

/**
 * Base URL for uploads (same origin as API, without /api)
 */
const getUploadsBaseUrl = () => {
  try {
    const base = API_BASE_URL.replace(/\/api\/?$/, '');
    return base || new URL(API_BASE_URL).origin;
  } catch {
    return '';
  }
};

/**
 * Full URL for a Mini Library asset (cover image or PDF).
 * Download link = "https://domain.com/" + link from database (relative path)
 */
export const getBookAssetUrl = (path) => {
  if (!path) return '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  return origin ? `${origin}/${normalized}` : path;
};

/**
 * Mini Library API endpoints
 */
export const miniLibraryAPI = {
  /**
   * Get categories with book count for a major
   * @param {string} major - english, korea, etc. Default: english
   */
  getCategories: (major = 'english') =>
    fetchAPI(`/mini-library/categories?major=${encodeURIComponent(major)}`),

  /**
   * Get books in a category
   * @param {string} major - english, korea, etc.
   * @param {string} category - Category name
   */
  getBooks: (major, category) =>
    fetchAPI(`/mini-library/books?major=${encodeURIComponent(major)}&category=${encodeURIComponent(category)}`),
};

/**
 * Languages API endpoints
 */
export const languagesAPI = {
  /**
   * Get all supported languages
   */
  getAll: () => fetchAPI('/languages/get'),
};

/**
 * Vocab Learning API endpoints
 */
export const vocabLearningAPI = {
  /**
   * Get available languages for vocabulary learning
   */
  getLanguages: () => fetchAPI('/vocab-learning/get-languages'),

  /**
   * Get decks filtered by major or language
   * @param {string} major - Optional major filter (english, korea, etc.)
   * @param {number} languageId - Optional language ID (for backward compatibility)
   * @param {number} userId - Optional user ID for progress data
   */
  getDecks: (major = null, languageId = null, userId = null) => {
    const params = new URLSearchParams();
    if (major) {
      params.append('major', major);
    } else if (languageId) {
      params.append('languageId', languageId);
    }
    if (userId) params.append('userId', userId);
    return fetchAPI(`/vocab-learning/get-decks?${params}`);
  },
  
  /**
   * Get learning cards for a session
   * @param {number} deckId - Deck ID
   * @param {number} wordCount - Number of words (default: 10)
   */
  getCards: (deckId, wordCount = 10) => {
    const params = new URLSearchParams({
      deckId: deckId,
      wordCount: wordCount,
    });
    return authFetchAPI(`/vocab-learning/get-cards?${params}`);
  },
  
  /**
   * Rate a word (SM2 algorithm)
   * @param {number} cardId - Card ID
   * @param {number} quality - Quality rating (0-5)
   */
  rateWord: (cardId, quality) => {
    return postAPI('/vocab-learning/rate-word', {
      cardId: cardId,
      quality: quality,
    });
  },
  
  /**
   * Skip a word
   * @param {number} cardId - Card ID
   * @param {number} deckId - Deck ID
   * @param {string} reason - Reason for skipping (default: 'already_know')
   * @param {array} sessionCardIds - Array of card IDs in current session
   */
  skipWord: (cardId, deckId, reason = 'already_know', sessionCardIds = []) => {
    return postAPI('/vocab-learning/skip-word', {
      cardId: cardId,
      deckId: deckId,
      reason: reason,
      sessionCardIds: sessionCardIds, // API expects array, JSON.stringify handled by postAPI if needed? No, postAPI stringifies the whole body.
    });
  },

  /**
   * Get comprehensive vocabulary learning progress
   */
  getVocabProgress: () => authFetchAPI('/vocab-learning/get-vocab-progress'),
};

/**
 * Discussion/Posts API endpoints
 */
export const discussionAPI = {
  /**
   * Get discussion posts
   * @param {string} category - Category (english/korea)
   * @param {number} page - Page number for pagination
   * @param {string} userId - Optional user ID for like status
   */
  get: (category = 'english', page = 1, limit = 15, userId = null) => {
    let params = `category=${category}&page=${page}&limit=${limit}`;
    if (userId) params += `&userId=${userId}`;
    return fetchAPI(`/discussions/get?${params}`);
  },
  
  /**
   * Get comments for a post
   * @param {number} postId - Post ID
   * @param {number} page - Page number for pagination
   * @param {number} limit - Items per page
   * @param {string} userId - Optional user ID for like status
   */
  getComments: (target, page = 1, limit = 20, userId = null) => {
    const targetParams = new URLSearchParams();
    if (typeof target === 'number' || typeof target === 'string') {
      // Backward compatibility: treat primitive as postId.
      targetParams.set('postId', String(target));
    } else if (target && typeof target === 'object') {
      if (target.postId != null) targetParams.set('postId', String(target.postId));
      if (target.lessonId != null) targetParams.set('lessonId', String(target.lessonId));
      if (target.targetType != null) targetParams.set('targetType', String(target.targetType));
      if (target.targetId != null) targetParams.set('targetId', String(target.targetId));
    }

    let params = `${targetParams.toString()}&page=${page}&limit=${limit}`;
    if (userId) params += `&userId=${userId}`;
    return fetchAPI(`/comments/get?${params}`);
  },
  
  /**
   * Get single post detail with comments
   * @param {number} postId - Post ID
   * @param {string} userId - Optional user ID for like status
   */
  getPostDetail: (postId, userId = null) => {
    let params = `postId=${postId}`;
    if (userId) params += `&userId=${userId}`;
    return fetchAPI(`/discussions/detail?${params}`);
  },
  
  /**
   * Get lesson post detail (allows hide = 1 posts)
   * @param {number} postId - Post ID
   * @param {string} userId - Optional user ID for like status
   */
  getLessonPostDetail: (postId, userId = null) => {
    let params = `postId=${postId}`;
    if (userId) params += `&userId=${userId}`;
    return fetchAPI(`/discussions/lesson-detail?${params}`);
  },

  /**
   * Create a new discussion post
   * @param {object} data - { body, category, image (base64) }
   */
  createPost: (data) => postAPI('/discussions/create', data),

  /**
   * Delete a post (owner only)
   * @param {number} postId
   */
  deletePost: (postId) => postAPI('/discussions/delete', { postId }),

  /**
   * Report a post
   * @param {number} postId
   */
  reportPost: (postId) => postAPI('/discussions/report', { postId }),

  /**
   * Hide a post (for current user only)
   * @param {number} postId
   */
  hidePost: (postId) => postAPI('/discussions/hide', { postId }),

  /**
   * Like/Unlike a post (toggle)
   * @param {number} postId
   * @returns {Promise<{success, count, isLiked}>}
   */
  likePost: (postId) => postAPI('/discussions/like', { postId }),

  /**
   * Like/Unlike a comment (toggle)
   * @param {number} commentId - comment.time
   */
  likeComment: (commentId) => postAPI('/comments/like', {commentId: commentId }),

  /**
   * Delete a comment (owner only)
   * @param {number} commentId - comment.time
   */
  deleteComment: (commentId) => postAPI('/comments/delete', { commentId: commentId }),

  /**
   * Update a comment (owner only)
   * @param {number} commentId - comment.time
   * @param {string} body - Updated comment text
   */
  updateComment: (commentId, body) => postAPI('/comments/update', { commentId: commentId, body }),

  /**
   * Create a comment or reply
   * @param {object} data - { postId, body, parent? }
   */
  createComment: (data) => {
    const payload = {
      body: data.body,
      parent: data.parent || 0,
    };
    if (data.postId != null) payload.postId = data.postId;
    if (data.lessonId != null) payload.lessonId = data.lessonId;
    if (data.targetType != null) payload.targetType = data.targetType;
    if (data.targetId != null) payload.targetId = data.targetId;
    if (data.image != null) payload.image = data.image;
    return postAPI('/comments/create', payload);
  },

  /**
   * Share a post
   * @param {number} postId - Original post ID to share
   */
  sharePost: (postId) => {
    const formData = new FormData();
    formData.append('postId', String(postId));
    return postFormDataAPI('/discussions/share', formData);
  },
};

/**
 * Communities API endpoints
 */
export const communitiesAPI = {
  /**
   * Get communities list
   * @param {string} major - Optional major filter (english, korea, etc.)
   */
  get: (major = null) => {
    const params = major ? `?major=${major}` : '';
    return fetchAPI(`/communities/get${params}`);
  },
};

/**
 * Additional Lessons API endpoints
 */
export const additionalLessonsAPI = {
  /**
   * Get courses with categories for a language channel
   * @param {string} channel - Language channel (english/korea)
   */
  getCourses: (channel = 'english') => fetchAPI(`/additional-lessons/courses?channel=${channel}`),
  
  /**
   * Get lessons for a specific category
   * @param {number} categoryId - Category ID
   */
  getLessons: (categoryId) => fetchAPI(`/additional-lessons/lessons?categoryId=${categoryId}`),
};

/**
 * Stats API endpoints
 */
export const statsAPI = {
  /**
   * Get home page stats for hero section
   * Returns: totalCourses, totalLessons, totalInstructors, totalStudents, avgRating, topInstructors
   */
  getHome: () => fetchAPI('/stats/home'),
  /**
   * Get About page stats (Our Reach)
   * Returns: instructors, courses, lectures, enrollments, languages, members
   */
  getAbout: () => fetchAPI('/about/stats'),
};

/**
 * Pinned Posts API endpoints
 */
export const pinnedPostsAPI = {
  /**
   * Get pinned posts for home page (all categories)
   */
  get: () => fetchAPI('/posts/pinned'),
};

/**
 * VIP Plan API endpoints
 */
export const vipPlanAPI = {
  /**
   * Get VIP plan data (pricing, payment methods, etc.)
   */
  get: () => fetchAPI('/vip-plan/get'),
};

/**
 * Rating API endpoints
 */
export const ratingAPI = {
  /**
   * Get all ratings for a course
   * @param {number} courseId - Course ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Results per page (default: 20)
   * @param {string} sort - Sorting criteria (recent, highest, lowest)
   */
  get: (courseId, page = 1, limit = 20, sort = 'recent') => fetchAPI(`/ratings?courseId=${courseId}&page=${page}&limit=${limit}&sort=${sort}`),

  /**
   * Create or update a rating for a course
   * @param {number} courseId - Course ID
   * @param {number} star - Rating (1-5)
   * @param {string} review - Review text (optional)
   */
  store: (courseId, star, review) => postAPI('/ratings/store', { courseId: courseId, star, review }),
  
  /**
   * Delete a rating
   * @param {number} id - Rating ID
   */
  delete: (id) => deleteAPI(`/ratings/${id}`),
  
  /**
   * Get latest reviews
   * @param {number} limit - Number of reviews to fetch (default: 6)
   */
  getLatest: (limit = 6) => fetchAPI(`/ratings/latest?limit=${limit}`),

  /**
   * Get rating breakdown for a course
   * @param {number} courseId - Course ID
   */
  getBreakdown: (courseId) => fetchAPI(`/ratings/breakdown?courseId=${courseId}`),
  
  // Deprecated methods kept for compatibility if needed, but pointing to new endpoints where possible or throwing error
  create: (courseId, star, review) => postAPI('/ratings/store', { courseId: courseId, star, review }),
  update: (id, star, review) => {
      console.warn('ratingAPI.update is deprecated. Use ratingAPI.store instead with courseId.');
      // We can't easily map update(id) to store(courseId) without knowing courseId.
      // Assuming the caller will switch to store() or we need to fetch the rating first to get courseId.
      throw new Error('ratingAPI.update is deprecated. Use ratingAPI.store instead.');
  },
};

/**
 * Apps API endpoints
 */
export const appsAPI = {
  /**
   * Get active apps (with active_course > 0)
   */
  get: () => fetchAPI('/apps/get'),
};

/**
 * Friends API endpoints (add friend, requests, confirm, unfriend, status)
 */
export const friendsAPI = {
  /**
   * Send friend request (or unsend if already sent)
   * @param {number} otherId - userId of the user to add
   * @param {string} major - english | korea | chinese | japanese | russian
   */
  addRequest: (otherId) =>
    postAPI('/friends/add', { otherId }),

  /**
   * Remove an incoming friend request (decline)
   * @param {number} otherId - userId of the person who sent the request
   */
  removeRequest: (otherId) =>
    postAPI('/friends/remove-request', { otherId }),

  /**
   * Accept friend request
   * @param {number} otherId - userId of the person who sent the request
   */
  confirm: (otherId) =>
    postAPI('/friends/confirm', { otherId }),

  /**
   * Unfriend a user
   * @param {number} otherId - userId
   */
  unfriend: (otherId) =>
    postAPI('/friends/unfriend', { otherId }),

  /**
   * Search for users (authenticated)
   * @param {string} query - search term
   * @param {number} page
   * @param {number} limit
   */
  search: (query, page = 1, limit = 20) =>
    authFetchAPI(`/friends/search?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`),

  /**
   * Get friend list for a user (public). Supports pagination.
   * @param {number} userId - userId
   * @param {string} major
   * @param {{ page?: number, limit?: number }} [params] - optional pagination
   */
  getFriends: (page = 1, limit = 20) => {
    return authFetchAPI(`/friends/get-friends?page=${page}&limit=${limit}`);
  },

  /**
   * Get my incoming requests + people you may know (authenticated)
   */
  getRequests: () =>
    authFetchAPI(`/friends/get-requests`),

  /**
   * Get people you may know (authenticated)
   * @param {{ page?: number, limit?: number }} [params] - optional pagination
   */
  getPeopleYouMayKnow: (page = 1, limit = 20) =>
    authFetchAPI(`/friends/people-you-may-know?page=${page}&limit=${limit}`),

  /**
   * Get friend status between me and another user (authenticated)
   * @param {number} otherId - userId
   * @returns {Promise<{ success, status: 'friend'|'pending_sent'|'pending_received'|'none' }>}
   */
  getStatus: (otherId) =>
    authFetchAPI(`/friends/get-status?otherId=${otherId}`),

  /**
   * Block a user (authenticated)
   * @param {number} otherId - userId of user to block
   * @returns {Promise<{ success, action: 'blocked'|'already_blocked' }>}
   */
  block: (otherId) =>
    postAPI('/friends/block', { otherId }),

  /**
   * Check if a user is blocked (either direction)
   * @param {number} otherId - userId
   * @returns {Promise<{ success, blocked: boolean, blocked_by_me: boolean, blocked_by_other: boolean }>}
   */
  checkBlock: (otherId) =>
    authFetchAPI(`/friends/check-block?otherId=${otherId}`),

  /**
   * Unblock a user (authenticated)
   * @param {number} otherId - userId of user to unblock
   * @returns {Promise<{ success, action: 'unblocked'|'not_blocked' }>}
   */
  unblock: (otherId) =>
    postAPI('/friends/unblock', { otherId }),
  /**
   * Get list of blocked users (authenticated)
   * @returns {Promise<{ success, data: Array }>}
   */
  getBlocked: () =>
    authFetchAPI('/friends/get-blocked'),
};

/**
 * Chat API (conversations + messages). Uses userId as user id (numeric).
 */
export const chatAPI = {
  getConversations: () =>
    authFetchAPI(`/chat/conversations`),

  getConversation: (conversationId) =>
    authFetchAPI(`/chat/conversations?id=${conversationId}`),

  createConversation: (user1Id, user2Id, major = null) => {
    const payload = { user1_id: Number(user1Id), user2_id: Number(user2Id) };
    if (major) payload.major = major;
    return postFormAPI('/chat/conversations', payload);
  },

  getMessages: (conversationId, major = null, params = {}) => {
    const search = new URLSearchParams({ conversation_id: conversationId });
    if (major) search.set('major', major);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.before_id != null) search.set('before_id', String(params.before_id));
    if (params.after_id != null) search.set('after_id', String(params.after_id));
    return authFetchAPI(`/chat/messages?${search.toString()}`);
  },

  sendMessage: (conversationId, senderId, major = null, payload = {}) => {
    const body = {
      conversation_id: Number(conversationId),
      sender_id: Number(senderId),
      message_type: payload.message_type || 'text',
      message_text: payload.message_text || '',
      file_path: payload.file_path || '',
      file_size: payload.file_size || '',
    };
    if (major) body.major = major;
    return postFormAPI('/chat/messages', body);
  },

  deleteConversation: (conversationId) =>
    deleteAPI(`/chat/conversations?id=${conversationId}`),

  markRead: (conversationId, userId, major = null) => {
    const body = { conversation_id: Number(conversationId), user_id: Number(userId) };
    if (major) body.major = major;
    return postFormAPI('/chat/mark-read', body);
  },

  uploadImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return postFormDataAPI('/chat/upload-image', formData);
  },
};

/**
 * User Profile API endpoints
 */
export const userAPI = {
  /**
   * Get public profile + posts for any user
   * @param {string} userId - User phone (learner_phone)
   * @param {number} page - Page number for posts pagination
   */
  getProfile: (userId, page = 1, tab = 'posts') => {
    let params = `id=${userId}&page=${page}&tab=${tab}`;
    return fetchAPI(`/users/profile?${params}`);
  },
  /**
   * Get current authenticated user's enrolled courses and progress
   */
  getMyLearning: () => authFetchAPI('/users/my-learning'),
  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @param {File} profileData.profileImage - Profile image file (optional)
   * @param {File} profileData.coverImage - Cover image file (optional)
   * @param {string} profileData.name - User name (required)
   * @param {string} profileData.bio - Bio text (optional)
   * @param {string} profileData.work - Work (optional)
   * @param {string} profileData.education - Education (optional)
   * @param {string} profileData.region - Region (optional)
   */
  updateProfile: (profileData) => {
    const formData = new FormData();
    
    if (profileData.name) formData.append('name', profileData.name);
    if (profileData.bio !== undefined) formData.append('bio', profileData.bio || '');
    if (profileData.work !== undefined) formData.append('work', profileData.work || '');
    if (profileData.education !== undefined) formData.append('education', profileData.education || '');
    if (profileData.region !== undefined) formData.append('region', profileData.region || '');
    
    if (profileData.profileImage) {
      formData.append('profileImage', profileData.profileImage);
    }
    if (profileData.coverImage) {
      formData.append('coverImage', profileData.coverImage);
    }

    return postFormDataAPI('/users/update', formData);
  },
  /**
   * Change user password
   * @param {Object} data - { currentPassword, newPassword }
   */
  changePassword: (data) => postAPI('/users/change-password', data),
  /**
   * Delete user account
   * @param {string} password - User password for confirmation
   */
  deleteAccount: (password) => postAPI('/users/delete-account', { password }),
};

/**
 * Notification API endpoints
 */
export const notificationAPI = {
  /**
   * Get notifications for authenticated user
   * @param {Object} [params] - optional: { major, limit } (e.g. { major: 'english', limit: 150 })
   */
  get: (params) => {
    const qs = params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return authFetchAPI('/notifications/get' + qs);
  },

  /**
   * Mark all notifications as read
   */
  markRead: () => postAPI('/notifications/mark-seen'),
  markSeen: (notificationId = null) =>
    postAPI('/notifications/mark-seen', notificationId ? { notificationId } : {}),

  /**
   * Mark a single notification as seen (on click)
   * @param {number} notificationId - notification id from get()
   */
  markOneRead: (notificationId) =>
    postAPI('/notifications/mark-one-read', { notificationId }),
};

/**
 * Auth API endpoints
 */
export const authAPI = {
  /**
   * Login with phone or email + password
   */
  login: (identifier, password) => {
    const isEmail = identifier.includes('@');
    const payload = { password };
    if (isEmail) {
      payload.email = identifier;
    } else {
      payload.phone = identifier;
    }
    return postAPI('/auth/login', payload);
  },

  /**
   * Register a new account
   */
  register: (data) => postAPI('/auth/register', data),

  /**
   * Get web-flow social login redirect URL
   */
  getSocialRedirectUrl: (provider, params = {}) => {
    const url = new URL(`${API_BASE_URL}/auth/social/${encodeURIComponent(provider)}/redirect`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  },

  /**
   * Check if email or phone is already registered
   */
  checkAccount: (payload) => postAPI('/auth/check-account', payload),

  /**
   * Request password reset email (see docs/api/auth_routes.md)
   */
  forgotPassword: (email) => postAPI('/auth/forgot-password', { email }),

  /**
   * Submit 6-digit OTP from email + new password (not logged in; see auth_routes.md)
   */
  confirmForgotPassword: ({ email, code, password, passwordConfirmation }) =>
    postAPI('/auth/confirm-forgot-password', {
      email,
      code,
      password,
      passwordConfirmation,
    }),

  /**
   * Change password while logged in (requires Bearer token; see auth_routes.md)
   */
  resetPasswordWhileLoggedIn: ({ oldPassword, password, passwordConfirmation }) =>
    postAPI('/auth/reset-password', {
      oldPassword,
      password,
      passwordConfirmation,
    }),

  /**
   * Verify email with 6-digit OTP (aliases: code / otp)
   */
  verifyEmail: ({ email, code }) => postAPI('/auth/verify-email', { email, code }),

  /**
   * Resend email verification OTP (requires auth)
   */
  resendEmailVerification: () => postAPI('/auth/email/resend-verification', {}),

  /**
   * Get current authenticated user (validates token)
   */
  me: () => authFetchAPI('/auth/me'),

  /**
   * Logout (invalidate token)
   */
  logout: () => postAPI('/auth/logout'),
};

export const paymentAPI = {
  submitPayment: (formData) => postFormDataAPI('/payments/paid', formData),
};

export default {
  course: courseAPI,
  instructor: instructorAPI,
  videoChannel: videoChannelAPI,
  song: songAPI,
  discussion: discussionAPI,
  additionalLessons: additionalLessonsAPI,
  stats: statsAPI,
  pinnedPosts: pinnedPostsAPI,
  vipPlan: vipPlanAPI,
  apps: appsAPI,
  user: userAPI,
  friends: friendsAPI,
  notification: notificationAPI,
  auth: authAPI,
  languages: languagesAPI,
  vocabLearning: vocabLearningAPI,
  payment: paymentAPI,
};
